import { useCallback, useEffect, useState } from "react"
import { Link, useParams, useSearchParams } from "react-router-dom"
import { getShared } from "../api"
import type { Block } from "../contract"
import { Wordmark } from "../ds"
import { useTurnStore } from "../store"
import { AnswerCard } from "../answer/AnswerCard"
import { AnswerSections } from "../answer/AnswerSections"
import { Glance } from "../answer/Glance"
import { SourcePanel } from "../answer/SourcePanel"
import { weigh, weighingLine } from "../answer/derive"
import { contextParts, shortDate, takeawayFor } from "../answer/format"
import { useAnswerSession } from "../answer/session"
import { usePageTitle } from "../answer/hooks"
import { rupees } from "../answer/blocks/money"
import { blocksFor, parseShared, sharedDay, type Shared } from "./snapshot"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import "./shared.css"

type Load = { state: "loading" } | { state: "gone" } | { state: "error" } | { state: "ready"; shared: Shared }

/**
 * A shared answer at /m/:token, read without signing in. Opens on the client view when the
 * sharer chose it (Client-View-v3), else on the full research; either links to the other.
 */
export default function SharedPage() {
  const { token = "" } = useParams()
  const [params] = useSearchParams()
  const [load, setLoad] = useState<Load>({ state: "loading" })
  const key = `shared-${token}`

  useEffect(() => {
    let live = true
    setLoad({ state: "loading" })
    getShared(token)
      .then((data) => {
        if (!live) return
        const shared = parseShared(key, data)
        if (!shared) return setLoad({ state: "error" })
        useTurnStore.setState((s) => ({ turns: { ...s.turns, [key]: shared.view }, order: [key] }))
        setLoad({ state: "ready", shared })
      })
      .catch((err: { status?: number }) => live && setLoad({ state: err?.status === 404 ? "gone" : "error" }))
    return () => {
      live = false
      useTurnStore.setState({ turns: {}, order: [] })
      useAnswerSession.getState().openSource(null)
    }
  }, [token, key])

  const print = params.get("print") === "1"
  useEffect(() => {
    if (load.state !== "ready" || !print) return
    const t = window.setTimeout(() => window.print(), 400)
    return () => window.clearTimeout(t)
  }, [load.state, print])

  usePageTitle(load.state === "ready" ? load.shared.view.head?.title || "Shared answer" : undefined)

  if (load.state !== "ready") {
    return (
      <div className="na2 na4 shp">
        <Bar />
        <main className="shp-msg">
          {load.state === "loading" ? <p role="status">Opening the shared answer…</p> : null}
          {load.state === "gone" ? (
            <>
              <h1>This link does not exist or was withdrawn.</h1>
              <p>Ask whoever sent it for a new one, or <Link to="/ask">ask Nira your own question</Link>.</p>
            </>
          ) : null}
          {load.state === "error" ? (
            <>
              <h1>This answer could not be opened.</h1>
              <p>Try again in a moment.</p>
            </>
          ) : null}
        </main>
      </div>
    )
  }
  const { shared } = load
  const full = params.get("view") === "full" || (!shared.clientView && params.get("view") !== "client")
  return full ? <FullView sharedKey={key} shared={shared} /> : <ClientView shared={shared} />
}

function Bar() {
  return (
    <header className="shp-bar">
      <Link to="/" aria-label="Nira home" className="shp-home"><Wordmark /></Link>
      <Link to="/ask" className="shp-ask">Ask your own</Link>
    </header>
  )
}

function Byline({ shared }: { shared: Shared }) {
  const who = shared.author ? [shared.author.name, shared.author.firm].filter(Boolean).join(", ") : ""
  const day = sharedDay(shared.sharedAt)
  return (
    <p className="shp-by">
      {who ? `Shared by ${who}` : "Shared from Nira"}
      {day ? ` · ${day}` : ""} · answered for {shared.context.side === "revenue" ? "the department" : "a taxpayer"}
      {shared.context.state ? ` in ${shared.context.state}` : ""}
    </p>
  )
}

function FullView({ sharedKey, shared }: { sharedKey: string; shared: Shared }) {
  const view = useTurnStore((s) => s.turns[sharedKey]) ?? shared.view
  const source = useAnswerSession((s) => s.source)
  const closeSource = useCallback(() => useAnswerSession.getState().openSource(null), [])
  const plan = view.plan ?? []
  const takeaways = plan.map((p) => takeawayFor(p, view.sections[p.id]?.blocks, {}, sharedKey))
  return (
    <div className={`na2 na4 shp${source ? " srcopen" : ""}`}>
      <Bar />
      <div className="a4">
        <div className="a4-col">
          <Byline shared={shared} />
          {shared.clientView ? <p className="shp-switch"><Link to="?view=client">Open the client view</Link></p> : null}
          <div className="uq"><p><span className="lab">Asked</span> <span className="uq-text">{shared.question}</span></p></div>
          {view.head ? (
            <AnswerCard
              id={`ans-${sharedKey}`}
              head={view.head}
              onContext={() => undefined}
              weighing={weighingLine(weigh(view.sections))}
              readOnly
            />
          ) : null}
          <Glance sections={view.sections} />
          <AnswerSections
            turn={sharedKey}
            plan={plan}
            sections={view.sections}
            takeaways={takeaways}
            sources={view.sources}
            running={false}
            limits={view.limits}
          />
          <p className="disc">Research, not legal advice. Check the cited text before you rely on it.</p>
        </div>
      </div>
      <SourcePanel onClose={closeSource} />
    </div>
  )
}

const itemsOf = (blocks: Block[]): string[] =>
  blocks.flatMap((b) => (b.type === "checklist" ? b.items : b.type === "list" ? b.items : []))

/** The client view (Client-View-v3): plain words, rupees and dates; the research one link away. */
function ClientView({ shared }: { shared: Shared }) {
  const { view } = shared
  const head = view.head
  const stake = blocksFor(view, "STAKE")
  const exposure = stake.find((b): b is Extract<Block, { type: "exposure" }> => b.type === "exposure")
  const action = blocksFor(view, "ACTION")
  const steps = itemsOf(action)
  const dates = action.filter((b): b is Extract<Block, { type: "deadline" }> => b.type === "deadline")
  const matter = contextParts(head?.context).join(" · ")
  const send = () => {
    const url = window.location.href
    window.open(`https://wa.me/?text=${encodeURIComponent(`${head?.short ?? ""}\n${url}`)}`, "_blank")
  }
  return (
    <div className="na2 na4 shp shc">
      <Bar />
      <main className="shc-main">
        <div className="shc-for">
          {shared.author ? (
            <p>From <strong>{shared.author.name}</strong>{shared.author.firm ? `, ${shared.author.firm}` : ""}{shared.sharedAt ? ` · ${sharedDay(shared.sharedAt)}` : ""}</p>
          ) : (
            <p>{sharedDay(shared.sharedAt)}</p>
          )}
          {head?.title ? <p>On {head.title}{matter ? ` (${matter})` : ""}</p> : null}
        </div>
        <section className="shc-sec">
          <p className="shc-k">Where you stand</p>
          <h1>{head?.short}</h1>
          {head?.lede ? <p className="shc-read">{head.lede}</p> : null}
        </section>
        {exposure ? (
          <section className="shc-sec">
            <h2>What it could cost</h2>
            <div className="shc-amts">
              {exposure.rows.map((r, i) => (
                <div className="shc-amt" key={i}>
                  <span>{r.label}</span>
                  <span className="m">{rupees(r.amount_inr)}</span>
                </div>
              ))}
              {exposure.total ? (
                <div className="shc-amt total">
                  <span>{exposure.total.label}</span>
                  <span className="m">{rupees(exposure.total.amount_inr)}</span>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
        {steps.length ? (
          <section className="shc-sec">
            <h2>What to do</h2>
            <ol className="shc-steps">{steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </section>
        ) : null}
        {dates.length ? (
          <section className="shc-sec shc-dates">
            {dates.map((d, i) => (
              <div key={i}><p className="shc-k">{d.label || "Reply by"}</p><p className="shc-date">{shortDate(d.date)}</p></div>
            ))}
          </section>
        ) : null}
        <p className="shc-foot">
          A summary {shared.author ? "from your adviser, " : ""}prepared with research on Nira. Research, not legal advice.{" "}
          <Link to="?view=full">The full research, with sources</Link>
        </p>
        <div className="shc-acts">
          <button type="button" className="pro-btn ghost" onClick={() => window.print()}>Save as PDF</button>
          <button type="button" className="pro-btn" onClick={send}>Send on WhatsApp</button>
        </div>
      </main>
    </div>
  )
}
